import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { NativeInvoiceAdjustmentMode } from "./nativeInvoiceFormTypes";
import { formatMoney } from "./formatMoney";
import styles from "./SidePanel.module.scss";

const ADJUSTMENT_MODE_OPTIONS: Array<{
    value: NativeInvoiceAdjustmentMode;
    label: string;
}> = [
    { value: "percent", label: "%" },
    { value: "amount", label: "$" },
];

function formatTotalsPercent(value: number): string {
    return `${value.toLocaleString("en-US", {
        maximumFractionDigits: 2,
    })}%`;
}

export const TotalsTextRow: React.FC<{
    label: string;
    value: string;
    isGrand?: boolean;
}> = ({ label, value, isGrand }) => (
    <div
        className={classNames(styles.totalsRow, {
            [styles.totalsRowGrand]: isGrand,
        })}
    >
        <span className={styles.totalsLabel}>{label}</span>
        <span
            className={classNames(styles.totalsValue, styles.totalsValueWide)}
        >
            {value}
        </span>
    </div>
);

export const TotalsAmountRow: React.FC<{
    label: string;
    value: number;
    mode: NativeInvoiceAdjustmentMode;
    onChange: (value: number) => void;
    onModeChange: (mode: NativeInvoiceAdjustmentMode) => void;
    isDisabled?: boolean;
    computedAmount?: number;
    computedPercent?: number;
}> = ({
    label,
    value,
    mode,
    onChange,
    onModeChange,
    isDisabled,
    computedAmount,
    computedPercent,
}) => {
    const [prevValue, setPrevValue] = useState(value);
    const [localValue, setLocalValue] = useState<number | undefined>(
        value === 0 ? undefined : value,
    );
    if (value !== prevValue) {
        setPrevValue(value);
        setLocalValue(value === 0 ? undefined : value);
    }

    const handleValueChange = useCallback(
        (values: { floatValue: number | undefined }) => {
            setLocalValue(values.floatValue);
        },
        [],
    );

    const handleBlur = useCallback(() => {
        onChange(localValue ?? 0);
    }, [localValue, onChange]);

    const isPercent = mode === "percent";

    return (
        <div className={styles.totalsRow}>
            <span className={styles.totalsLabel}>{label}</span>
            <ToggleGroup.Root<NativeInvoiceAdjustmentMode>
                value={mode}
                onValueChange={(nextMode) => {
                    if (nextMode === null) {
                        return;
                    }
                    onModeChange(nextMode);
                }}
                size="lg"
                className={styles.totalsModeToggle}
            >
                {ADJUSTMENT_MODE_OPTIONS.map((option) => (
                    <ToggleGroup.Item key={option.value} value={option.value}>
                        {option.label}
                    </ToggleGroup.Item>
                ))}
            </ToggleGroup.Root>
            {isDisabled ? (
                <span className={styles.totalsValue}>
                    {isPercent
                        ? formatTotalsPercent(computedPercent ?? 0)
                        : formatMoney(computedAmount ?? 0)}
                </span>
            ) : (
                <NumberFormat
                    className={styles.totalsInput}
                    value={localValue}
                    onValueChange={handleValueChange}
                    onBlur={handleBlur}
                    prefix={isPercent ? "" : "$"}
                    suffix={isPercent ? "%" : ""}
                    placeholder={isPercent ? "0%" : "$0.00"}
                    decimalScale={2}
                    fixedDecimalScale={!isPercent}
                    thousandSeparator
                    allowNegative
                />
            )}
        </div>
    );
};
